/**
 * Pure helpers behind the dashboard's monthly chart: shape the server's
 * per-activity series into chart rows, and label the months. No React here.
 */
import type { MonthAccountChange, MonthRun, MonthlyPoint, MonthlyStats } from "./api";
// both: short names label the chart and the calendar (juin / juil.), the long
// one titles a month's drill-down
import { monthNames, monthShortNames } from "./params";
import { categoryOf } from "./states";

/** The activity toggle: every activity side by side, one of them, or their total. */
export const ALL_ACTIVITIES = "ALL";

export type MonthlyRow = {
  period: number;
  month: string;
  /** one column per charted series; absent when the month has no figure */
  [series: string]: number | string | undefined;
};

/** The toggle's choices, in order: All, each activity, Total. */
export function activityChoices(stats: Pick<MonthlyStats, "entities" | "total_series">): string[] {
  return [ALL_ACTIVITIES, ...stats.entities, stats.total_series];
}

/** The series the chart carries for a toggle choice. */
export function seriesFor(
  stats: Pick<MonthlyStats, "entities" | "total_series">,
  activity: string,
): string[] {
  if (activity === ALL_ACTIVITIES) return stats.entities;
  return [activity];
}

/** Rows for the chart: one per month, one column per carried series. */
export function chartRows(
  stats: Pick<MonthlyStats, "entities" | "total_series" | "series">,
  activity: string,
  locale: string,
): MonthlyRow[] {
  const names = monthShortNames(locale);
  const carried = seriesFor(stats, activity);
  return Array.from({ length: 12 }, (_, index) => {
    const row: MonthlyRow = { period: index + 1, month: names[index] };
    for (const name of carried) {
      const point = stats.series[name]?.[index];
      if (point?.amount != null) row[name] = Number(point.amount);
    }
    return row;
  });
}

/** The point behind a chart cell, for the tooltip and the click-through. */
export function pointAt(
  stats: Pick<MonthlyStats, "series">,
  series: string,
  period: number,
): MonthlyPoint | null {
  return stats.series[series]?.[period - 1] ?? null;
}

/** True when at least one figure exists among the carried series. */
export function hasFigures(
  stats: Pick<MonthlyStats, "entities" | "total_series" | "series">,
  activity: string,
): boolean {
  return seriesFor(stats, activity).some(
    (name) => stats.series[name]?.some((p) => p.amount != null) ?? false,
  );
}

/** Compact money for axis ticks: 1 234 567 → "1,2 M", 12 345 → "12 k". */
export function compactAmount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 }).format(
    value,
  );
}

export function fullAmount(value: number, locale: string, currency?: string | null): string {
  const text = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
  return currency ? `${text} ${currency}` : text;
}

/** "Octobre 2025" / "October 2025" — the drill-down's title. */
export function periodTitle(year: number, period: number, locale: string): string {
  return `${monthNames(locale)[period - 1]} ${year}`;
}

/** The uploads launched for a month, newest first; empty when none. */
export function uploadsOf(
  stats: Pick<MonthlyStats, "calendar">,
  period: number,
): MonthRun[] {
  // a backend older than this screen answers without the list
  return stats.calendar.find((m) => m.period === period)?.runs ?? [];
}

/** True when the figure at `index` differs from the previous upload's. */
export function cellChanged(values: (string | null)[], index: number): boolean {
  return index > 0 && values[index] !== values[index - 1];
}

/**
 * The account rows the drill-down table shows: one activity (CEM, RMC or
 * AGG) or all of them, the changed ones only (the default once two uploads
 * exist), narrowed by a free-text search over the account number and its
 * label. Changed rows come first.
 */
export function changeRows(
  accounts: MonthAccountChange[],
  options: { changedOnly: boolean; search?: string; entity?: string | null },
): MonthAccountChange[] {
  const needle = (options.search ?? "").trim().toLowerCase();
  return accounts
    .filter((row) => !options.entity || row.entity === options.entity)
    .filter((row) => !options.changedOnly || row.changed)
    .filter(
      (row) =>
        !needle ||
        row.fs_item.toLowerCase().includes(needle) ||
        row.label.toLowerCase().includes(needle),
    )
    .sort((a, b) => Number(b.changed) - Number(a.changed));
}

/**
 * The uploads of a month that finished well but produced no figures, and
 * name the tool to run on their output for the figures to appear: a P&L
 * inputs run by hand, waiting for the mapping.
 */
export function awaitingNextTool(runs: MonthRun[]): MonthRun[] {
  return runs.filter(
    (run) =>
      run.harvested === false &&
      run.harvests === false &&
      Boolean(run.next_tool) &&
      Boolean(run.output_artifact_id) &&
      ["success", "warning"].includes(categoryOf(run.state) ?? ""),
  );
}

/** Signed money for the delta column: "+1 234,50 MAD" / "−20,50 MAD". */
export function signedAmount(value: string | null, locale: string, currency?: string | null): string {
  if (value == null) return "—";
  const number = Number(value);
  const text = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    signDisplay: "exceptZero",
  }).format(number);
  return currency ? `${text} ${currency}` : text;
}
